import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const STORAGE_KEY = 'tripFilter'
const STORAGE_EVENT = 'tripFilterChanged'

const getDefaultCriteria = () => ({
  year: String(new Date().getFullYear())
})

const readStoredCriteria = (fallback = {}) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return fallback

    return JSON.parse(stored)
  } catch {
    return fallback
  }
}

const useGenericListFilter = (
  items,
  getSearchText,
  initialCriteria = getDefaultCriteria()
) => {
  const initialCriteriaRef = useRef(initialCriteria)

  const [criteria, setCriteriaState] = useState(() =>
    readStoredCriteria(initialCriteriaRef.current)
  )

  const setCriteria = useCallback((nextCriteria) => {
    setCriteriaState((currentCriteria) => {
      const resolvedCriteria =
        typeof nextCriteria === 'function'
          ? nextCriteria(currentCriteria)
          : nextCriteria

      const normalizedCriteria = resolvedCriteria || {}

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(normalizedCriteria)
      )

      window.dispatchEvent(
        new CustomEvent(STORAGE_EVENT, {
          detail: normalizedCriteria
        })
      )

      return normalizedCriteria
    })
  }, [])

  useEffect(() => {
    const syncCriteria = (event) => {
      if (
        event.type === 'storage' &&
        event.key !== STORAGE_KEY
      ) {
        return
      }

      const nextCriteria =
        event.type === STORAGE_EVENT
          ? event.detail
          : readStoredCriteria(initialCriteriaRef.current)

      setCriteriaState(nextCriteria || {})
    }

    window.addEventListener(STORAGE_EVENT, syncCriteria)
    window.addEventListener('storage', syncCriteria)

    return () => {
      window.removeEventListener(STORAGE_EVENT, syncCriteria)
      window.removeEventListener('storage', syncCriteria)
    }
  }, [])

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const data = getSearchText(item)
      const itemDate = data.date
        ? data.date.slice(0, 10)
        : null

      if (
        criteria.vehicleId &&
        String(data.vehicleId) !== String(criteria.vehicleId)
      ) {
        return false
      }

      if (criteria.from && itemDate < criteria.from) {
        return false
      }

      if (criteria.to && itemDate > criteria.to) {
        return false
      }

      if (
        criteria.month &&
        !itemDate?.startsWith(criteria.month)
      ) {
        return false
      }

      if (
        criteria.year &&
        !itemDate?.startsWith(criteria.year)
      ) {
        return false
      }

      return true
    })
  }, [items, criteria, getSearchText])

  return {
    criteria,
    setCriteria,
    filteredItems
  }
}

export default useGenericListFilter